/**
 * PageHeader — one consistent header for every page.
 *
 * Usage:
 *   <PageHeader
 *     icon={<Landmark size={20} />}
 *     title="EMI Manager"
 *     subtitle="Track and manage your loan EMIs"
 *     color="indigo"          // orange | blue | indigo | emerald | purple | amber | rose | teal
 *   >
 *     <button>+ Add EMI</button>   // optional action buttons as children
 *   </PageHeader>
 */

const COLOR_MAP = {
  orange:  { bg: 'bg-orange-100 dark:bg-orange-950/40',  icon: 'text-orange-500',  border: 'border-orange-200 dark:border-orange-900' },
  blue:    { bg: 'bg-blue-100 dark:bg-blue-950/40',      icon: 'text-blue-500',    border: 'border-blue-200 dark:border-blue-900' },
  indigo:  { bg: 'bg-indigo-100 dark:bg-indigo-950/40',  icon: 'text-indigo-500',  border: 'border-indigo-200 dark:border-indigo-900' },
  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-950/40',icon: 'text-emerald-500', border: 'border-emerald-200 dark:border-emerald-900' },
  purple:  { bg: 'bg-purple-100 dark:bg-purple-950/40',  icon: 'text-purple-500',  border: 'border-purple-200 dark:border-purple-900' },
  amber:   { bg: 'bg-amber-100 dark:bg-amber-950/40',    icon: 'text-amber-500',   border: 'border-amber-200 dark:border-amber-900' },
  rose:    { bg: 'bg-rose-100 dark:bg-rose-950/40',      icon: 'text-rose-500',    border: 'border-rose-200 dark:border-rose-900' },
  teal:    { bg: 'bg-teal-100 dark:bg-teal-950/40',      icon: 'text-teal-500',    border: 'border-teal-200 dark:border-teal-900' },
  violet:  { bg: 'bg-violet-100 dark:bg-violet-950/40',  icon: 'text-violet-500',  border: 'border-violet-200 dark:border-violet-900' },
};

export default function PageHeader({ icon, title, subtitle, color = 'orange', children }) {
  const c = COLOR_MAP[color] || COLOR_MAP.orange;

  return (
    <div className={`sticky top-0 z-20 bg-white dark:bg-stone-900 border-b ${c.border} px-6 py-5`}>
      <div className="flex items-start justify-between gap-4">
        {/* Left: icon + title + subtitle */}
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-2xl ${c.bg} flex items-center justify-center shrink-0 ${c.icon}`}>
            {icon}
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-extrabold text-stone-900 dark:text-stone-100 leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-xs text-stone-400 dark:text-stone-500 mt-0.5 truncate">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Right: action buttons */}
        {children && (
          <div className="flex items-center gap-2 shrink-0">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
